import React, { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Spinner from 'react-bootstrap/Spinner';
import { Panel, PanelHeader, PanelBody } from '../../../../components/panel/panel';
import { getOperatorsDatatable } from '../../../../services/OperatorService';

const FIGURE_TYPE_DESCRIPTIONS = {
	'01': 'Operador',
	'02': 'Propietario',
	'03': 'Arrendador',
	'04': 'Notificado',
	'05': 'Integrante de Coordinados',
};

function Operators() {
	const [data, setData] = useState({ data: [], recordsTotal: 0, recordsFiltered: 0 });
	const [loading, setLoading] = useState(true);
	const [searchValue, setSearchValue] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [sortField, setSortField] = useState('id');
	const [sortDirection, setSortDirection] = useState('asc');

	// Debounce para la busqueda en tiempo real (400ms)
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchValue);
			setCurrentPage(1);
		}, 400);

		return () => clearTimeout(timer);
	}, [searchValue]);

	const fetchData = useCallback(async (page, search, sort, dir) => {
		try {
			setLoading(true);
			const response = await getOperatorsDatatable({
				start: (page - 1) * rowsPerPage,
				length: rowsPerPage,
				search: search,
				sort: sort,
				dir: dir,
			});
			setData(response || { data: [], recordsTotal: 0, recordsFiltered: 0 });
		} catch (error) {
			console.error('Error al obtener operadores:', error);
			const errorMsg = error.response?.data?.message || 'Error al cargar los datos de operadores';
			Swal.fire('Error', errorMsg, 'error');
		} finally {
			setLoading(false);
		}
	}, [rowsPerPage]);

	useEffect(() => {
		fetchData(currentPage, debouncedSearch, sortField, sortDirection);
	}, [fetchData, currentPage, debouncedSearch, sortField, sortDirection]);

	const handleSearch = () => {
		setCurrentPage(1);
		fetchData(1, searchValue, sortField, sortDirection);
	};

	const handleKeyPress = (event) => {
		if (event.key === 'Enter') {
			handleSearch();
		}
	};

	const handleSort = (column, direction) => {
		setSortField(column.sortField || 'id');
		setSortDirection(direction);
	};

	const columns = [
		{
			name: 'ID',
			selector: row => row.id,
			sortable: true,
			sortField: 'id',
			width: '70px',
		},
		{
			name: 'Nombre',
			selector: row => row.name,
			sortable: true,
			sortField: 'name',
			minWidth: '140px',
		},
		{
			name: 'Primer apellido',
			selector: row => row.first_surname,
			sortable: true,
			sortField: 'first_surname',
			minWidth: '140px',
		},
		{
			name: 'Segundo apellido',
			selector: row => row.second_surname || '',
			sortable: true,
			sortField: 'second_surname',
			minWidth: '140px',
			cell: row => row.second_surname || <span className="text-muted">-</span>,
		},
		{
			name: 'Tipo de figura',
			sortable: true,
			sortField: 'figure_type',
			minWidth: '200px',
			cell: row => {
				if (!row.figure_type) {
					return <span className="text-muted fst-italic">Sin asignar</span>;
				}
				const desc = row.figure_type_description || FIGURE_TYPE_DESCRIPTIONS[row.figure_type] || '';
				return <span>{row.figure_type} - {desc}</span>;
			},
		},
		{
			name: 'RFC',
			sortable: true,
			sortField: 'figure_rfc',
			minWidth: '150px',
			cell: row => row.figure_rfc ? (
				<span className="badge bg-light text-dark border font-monospace">{row.figure_rfc}</span>
			) : (
				<span className="text-muted fst-italic">Sin asignar</span>
			),
		},
		{
			name: 'No. de licencia',
			sortable: true,
			sortField: 'license_number',
			minWidth: '150px',
			cell: row => row.license_number ? (
				<span>{row.license_number}</span>
			) : (
				<span className="text-muted fst-italic">Sin asignar</span>
			),
		},
		{
			name: 'Editar',
			button: true,
			width: '80px',
			cell: row => (
				<Link to={`/settings/operators/edit/${row.id}`} className="btn btn-xs btn-outline-primary" title="Editar datos de Carta Porte">
					<i className="fa fa-pencil me-1" /> Editar
				</Link>
			),
		},
	];

	const customStyles = {
		headCells: {
			style: {
				color: '#202124',
				fontSize: '14px',
				backgroundColor: '#EEEEEE',
				border: 'none',
				fontWeight: '600',
			},
		},
		cells: {
			style: {
				fontSize: '13px',
			},
		},
	};

	const paginationComponentOptions = {
		rowsPerPageText: 'Filas por página',
		rangeSeparatorText: 'de',
		selectAllRowsItem: false,
		selectAllRowsItemText: 'Todos',
	};

	return (
		<div>
			<div className="d-flex align-items-center mb-3">
				<div>
					<ol className="breadcrumb">
						<li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
						<li className="breadcrumb-item active">Operadores</li>
					</ol>
					<h1 className="page-header mb-0">Operadores</h1>
				</div>
			</div>

			<div className="row mb-3">
				<div className="col-xl-12">
					<Panel>
						<PanelHeader>Listado de Operadores</PanelHeader>
						<PanelBody>
							<DataTable
								title=""
								className="table"
								columns={columns}
								data={data.data}
								progressPending={loading}
								sortServer
								onSort={handleSort}
								pagination
								paginationServer
								paginationPerPage={rowsPerPage}
								paginationTotalRows={data.recordsTotal}
								paginationDefaultPage={currentPage}
								subHeader
								customStyles={customStyles}
								highlightOnHover
								pointerOnHover
								paginationComponentOptions={paginationComponentOptions}
								noDataComponent="Sin información por mostrar"
								progressComponent={
									<div className="py-4 text-center">
										<Spinner animation="border" role="status" variant="primary">
											<span className="visually-hidden">Cargando...</span>
										</Spinner>
									</div>
								}
								subHeaderComponent={
									<div className="row w-100">
										<div className="col-sm-12 col-md-4">
											<div className="input-group mb-2">
												<input
													type="text"
													className="form-control form-control-sm"
													placeholder="Buscar por nombre, apellido, RFC, etc..."
													value={searchValue}
													onChange={e => setSearchValue(e.target.value)}
													onKeyDown={handleKeyPress}
												/>
												<button
													type="button"
													className="btn btn-info btn-sm"
													onClick={handleSearch}
													title="Buscar"
												>
													<i className="fa fa-search"></i>
												</button>
											</div>
										</div>
									</div>
								}
								onChangePage={(page) => {
									setCurrentPage(page);
								}}
								onChangeRowsPerPage={(currentRowsPerPage) => {
									setRowsPerPage(currentRowsPerPage);
									setCurrentPage(1);
								}}
							/>
						</PanelBody>
					</Panel>
				</div>
			</div>
		</div>
	);
}

export default Operators;
